import type { NordicCountry } from '../hooks/useNordicComingSoon';
import type { HusketRolle } from './roleMemory';

/**
 * Hvilke land økten faktisk viser — og testerens egne av/på-brytere.
 *
 * HVORFOR DENNE FILEN: Land-tilgang ble regnet ut inne i App.tsx, samtidig som
 * profilen hadde en «Test Sverige»-bryter i localStorage som ingenting leste
 * lenger. Testere så derfor en bryter som sto AV mens Sverige i praksis var på —
 * og trodde de måtte skru den på selv. Nå bor regelen ett sted, den er ren, og
 * bryterne står PÅ som standard.
 *
 * PRINSIPP: bryterne kan bare SKJULE land man har fått via rollen (beta), aldri
 * åpne et land man ikke har tilgang til, og aldri skjule et land som er lansert
 * for alle. Derfor er de trygge å eksponere.
 */

/** Land som testere/admin kan slå av og på selv. Island har ingen data ennå. */
export const BETA_COUNTRIES = ['sweden', 'denmark', 'finland'] as const;
export type BetaCountry = (typeof BETA_COUNTRIES)[number];

export type BetaCountryPrefs = Record<BetaCountry, boolean>;

const NOKKEL = 'fricamp_beta_countries';

/** Sendes når en bryter endres, så App.tsx kan oppdatere kartet umiddelbart. */
export const BETA_COUNTRY_EVENT = 'fricamp-beta-countries';

/** Standard er PÅ: en tester skal se det som ikke er lansert uten å gjøre noe. */
export const DEFAULT_BETA_PREFS: BetaCountryPrefs = { sweden: true, denmark: true, finland: true };

export const readBetaCountryPrefs = (): BetaCountryPrefs => {
  try {
    const rå = typeof localStorage !== 'undefined' ? localStorage.getItem(NOKKEL) : null;
    if (!rå) return { ...DEFAULT_BETA_PREFS };
    const lagret = JSON.parse(rå) as Partial<Record<BetaCountry, unknown>>;
    // `!== false`: et manglende eller ødelagt felt betyr PÅ, aldri av.
    return {
      sweden: lagret?.sweden !== false,
      denmark: lagret?.denmark !== false,
      finland: lagret?.finland !== false,
    };
  } catch {
    return { ...DEFAULT_BETA_PREFS };
  }
};

export const setBetaCountryPref = (land: BetaCountry, på: boolean): BetaCountryPrefs => {
  const neste = { ...readBetaCountryPrefs(), [land]: på };
  try { localStorage.setItem(NOKKEL, JSON.stringify(neste)); } catch { /* privat modus */ }
  try { window.dispatchEvent(new Event(BETA_COUNTRY_EVENT)); } catch { /* ikke-nettleser */ }
  return neste;
};

export type CountryAccessStatus =
  | 'launched'  // lansert for alle — bryteren er ikke relevant
  | 'beta'      // rollen gir tilgang; brukeren kan slå den av
  | 'locked';   // rollen gir ikke tilgang (f.eks. utløpt testerperiode)

export const countryAccessStatus = (
  land: BetaCountry,
  launched: Partial<Record<NordicCountry, boolean>>,
  role: HusketRolle,
  ownerEmail = false,
): CountryAccessStatus => {
  if (launched?.[land] === true) return 'launched';
  return betaGrant(role, ownerEmail)[land] ? 'beta' : 'locked';
};

/** Hva rollen ALENE gir, før brukerens egne brytere. */
const betaGrant = (role: HusketRolle, ownerEmail: boolean): Record<NordicCountry, boolean> => {
  const full = ownerEmail || role.isAdmin;
  const isTesterActive = full || role.activeTester || role.isTester;
  return {
    sweden: isTesterActive,
    denmark: isTesterActive,
    finland: isTesterActive,
    iceland: full,
  };
};

/**
 * Landene økten skal vise: lansert for alle ELLER gitt av rollen og ikke slått
 * av av brukeren selv.
 */
export const resolveCountryAccess = (args: {
  launched: Partial<Record<NordicCountry, boolean>>;
  role: HusketRolle;
  ownerEmail?: boolean;
  prefs?: BetaCountryPrefs;
}): Record<NordicCountry, boolean> => {
  const { launched, role, ownerEmail = false } = args;
  const prefs = args.prefs ?? DEFAULT_BETA_PREFS;
  const grant = betaGrant(role, ownerEmail);
  const på = (land: NordicCountry): boolean => {
    if (launched?.[land] === true) return true;
    if (!grant[land]) return false;
    return land === 'iceland' ? true : prefs[land] !== false;
  };
  return {
    sweden: på('sweden'),
    denmark: på('denmark'),
    finland: på('finland'),
    iceland: på('iceland'),
  };
};
